/**
 * Generated by orval v7.9.0 🍺
 * Do not edit manually.
 * Go Chat Auth API
 * API for frontend and mobile apps
 * OpenAPI spec version: 1.0
 */
import { faker } from '@faker-js/faker';

import { HttpResponse, delay, http } from 'msw';

import { createAuthInstance } from '../../api-instance';
import type { BodyType } from '../../api-instance';
/**
 * Optional authentication intent for 2FA or additional verification
 */
export type GoChatBackendInternalModelsAuthResponseAuthIntent = {
  user?: GoChatBackendInternalModelsUser;
};

export interface GoChatBackendInternalModelsAuthResponse {
  /** JWT authentication token */
  auth_token?: string;
  /** Optional authentication intent for 2FA or additional verification */
  auth_intent?: GoChatBackendInternalModelsAuthResponseAuthIntent;
}

export interface GoChatBackendInternalModelsChangeEmailRequest {
  current_password: string;
  email: string;
}

export interface GoChatBackendInternalModelsChangePasswordRequest {
  current_password: string;
  new_password: string;
}

export interface GoChatBackendInternalModelsChannel {
  id?: string;
  is_active?: boolean;
  owner_id?: string;
  protocol?: GoChatBackendInternalModelsProtocol;
  status?: GoChatBackendInternalModelsChannelStatus;
}

export type GoChatBackendInternalModelsChannelStatus =
  (typeof GoChatBackendInternalModelsChannelStatus)[keyof typeof GoChatBackendInternalModelsChannelStatus];

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const GoChatBackendInternalModelsChannelStatus = {
  StatusUnauthorized: 'unauthorized',
  StatusPendingPayment: 'pending_payment',
  StatusQRInProgress: 'qr_in_progress',
  StatusRequiredScanQR: 'required_scan_qr',
  StatusAuthorized: 'authorized',
  StatusContactsSynced: 'contacts_synced',
  StatusChatsSynced: 'chats_synced',
  StatusReady: 'ready',
} as const;

export interface GoChatBackendInternalModelsError {
  code?: string;
  details?: string;
}

export interface GoChatBackendInternalModelsForgotPasswordRequest {
  email: string;
}

export interface ForgotPasswordResponse {
  token: string;
}

export type GoChatBackendInternalModelsProtocol =
  (typeof GoChatBackendInternalModelsProtocol)[keyof typeof GoChatBackendInternalModelsProtocol];

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const GoChatBackendInternalModelsProtocol = {
  ProtocolTelegramPersonal: 'telegram_personal',
  ProtocolTelegramBot: 'telegram_bot',
  ProtocolWhatsappGray: 'whatsapp_gray',
  ProtocolInfobip: 'infobip',
  ProtocolVk: 'vk',
} as const;

export interface GoChatBackendInternalModelsRegisterRequest {
  email: string;
  locale?: string;
  /** @minLength 6 */
  password: string;
  account_type: string;
  phone: string;
  accept_pd: boolean;
  accept_ula: boolean;
}

export interface GoChatBackendInternalModelsResendCodeRequest {
  token: string;
}

export interface GoChatBackendInternalModelsResetPasswordRequest {
  /**
   * @minLength 6
   * @maxLength 6
   * @pattern ^[0-9]{6}$
   */
  code: string;
  /** @minLength 6 */
  new_password: string;
  token: string;
  password_duplicate_agreement?: boolean;
}

export interface GoChatBackendInternalModelsSignInRequest {
  email: string;
  password: string;
}

export interface GoChatBackendInternalModelsUpdateMeRequest {
  locale?: string;
  timezone?: string;
  twofa_is_active?: boolean;
  phone?: string;
  /** hand-patched: regenerated on next spec sync */
  current_password?: string;
}

export interface GoChatBackendInternalModelsSettings {
  /** User ID */
  id?: string;
  /** User phone number */
  phone?: string;
  /** User email */
  email?: string;
  /** User locale preference */
  locale?: string;
  /** User timezone */
  timezone?: string;
  /** Whether the user account is active */
  isActive?: boolean;
  /** Timestamp when password was last changed */
  password_changed_at?: string;
  /** Whether password change is required */
  need_to_change_password?: boolean;
  /** Whether two-factor authentication is active */
  twofa_is_active?: boolean;
}

export interface GoChatBackendInternalModelsUser {
  account_id?: string;
  balance?: number;
  created_at?: string;
  email?: string;
  first_name?: string;
  id?: string;
  is_active?: boolean;
  last_activity_at?: string;
  last_name?: string;
  locale?: string;
  need_to_change_password?: boolean;
  password_changed_at?: string;
  phone?: string;
  role?: GoChatBackendPkgRbacRole;
  timezone?: string;
  f2a_is_active?: boolean;
}

export interface GoChatBackendInternalModelsVerifyAuthIntentRequest {
  code: string;
  token: string;
}

export type GoChatBackendPkgRbacRole =
  (typeof GoChatBackendPkgRbacRole)[keyof typeof GoChatBackendPkgRbacRole];

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const GoChatBackendPkgRbacRole = {
  RoleOwner: 'owner',
  RoleOperator: 'operator',
  RoleAuthorized: '?',
} as const;

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

/**
 * Retrieve user settings information using the user ID extracted from the JWT token
 * @summary Get user settings
 */
export const getAuthSettings = (
  options?: SecondParameter<typeof createAuthInstance>
) => {
  return createAuthInstance<GoChatBackendInternalModelsSettings>(
    { url: `/auth/settings`, method: 'GET' },
    options
  );
};

/**
 * Update user settings information using the user ID extracted from the JWT token
 * @summary Update user settings
 */
export const patchAuthSettings = (
  goChatBackendInternalModelsUpdateMeRequest: BodyType<GoChatBackendInternalModelsUpdateMeRequest>,
  options?: SecondParameter<typeof createAuthInstance>
) => {
  return createAuthInstance<void>(
    {
      url: `/auth/settings`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: goChatBackendInternalModelsUpdateMeRequest,
    },
    options
  );
};

/**
 * Changes the email address of the currently authenticated user. Requires current password for validation.
 * @summary Change user email
 */
export const patchChangeEmail = (
  goChatBackendInternalModelsChangeEmailRequest: BodyType<GoChatBackendInternalModelsChangeEmailRequest>,
  options?: SecondParameter<typeof createAuthInstance>
) => {
  return createAuthInstance<string>(
    {
      url: `/change-email`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: goChatBackendInternalModelsChangeEmailRequest,
    },
    options
  );
};

/**
 * Changes the password of the currently authenticated user. Requires current password for validation.
 * @summary Change user password
 */
export const patchChangePassword = (
  goChatBackendInternalModelsChangePasswordRequest: BodyType<GoChatBackendInternalModelsChangePasswordRequest>,
  options?: SecondParameter<typeof createAuthInstance>
) => {
  return createAuthInstance<string>(
    {
      url: `/change-password`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: goChatBackendInternalModelsChangePasswordRequest,
    },
    options
  );
};

/**
 * Send password reset email to user
 * @summary Initiate password reset
 */
export const postForgotPassword = (
  goChatBackendInternalModelsForgotPasswordRequest: BodyType<GoChatBackendInternalModelsForgotPasswordRequest>,
  options?: SecondParameter<typeof createAuthInstance>
) => {
  return createAuthInstance<ForgotPasswordResponse>(
    {
      url: `/forgot-password`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: goChatBackendInternalModelsForgotPasswordRequest,
    },
    options
  );
};

/**
 * Invalidate user session and clear auth cookies
 * @summary User logout
 */
export const postLogout = (
  options?: SecondParameter<typeof createAuthInstance>
) => {
  return createAuthInstance<void>({ url: `/logout`, method: 'POST' }, options);
};

/**
 * Refresh access token using refresh token from **cookie** (cookie name: `refresh_token`).  
**Example:**  
```http
POST /refresh  
Cookie: refresh_token=your_refresh_token_here
```

 * @summary Refresh tokens
 */
export const postRefresh = (
  options?: SecondParameter<typeof createAuthInstance>
) => {
  return createAuthInstance<void>({ url: `/refresh`, method: 'POST' }, options);
};

/**
 * Create a new user with email and password
 * @summary Register a new user
 */
export const postRegister = (
  goChatBackendInternalModelsRegisterRequest: BodyType<GoChatBackendInternalModelsRegisterRequest>,
  options?: SecondParameter<typeof createAuthInstance>
) => {
  return createAuthInstance<GoChatBackendInternalModelsAuthResponse>(
    {
      url: `/register`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: goChatBackendInternalModelsRegisterRequest,
    },
    options
  );
};

/**
 * Resend authentication code to user's email
 * @summary Resend verification code
 */
export const postResendCode = (
  goChatBackendInternalModelsResendCodeRequest: BodyType<GoChatBackendInternalModelsResendCodeRequest>,
  options?: SecondParameter<typeof createAuthInstance>
) => {
  return createAuthInstance<void>(
    {
      url: `/resend-code`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: goChatBackendInternalModelsResendCodeRequest,
    },
    options
  );
};

/**
 * Reset password using recovery token
 * @summary Reset user password
 */
export const postResetPassword = (
  goChatBackendInternalModelsResetPasswordRequest: BodyType<GoChatBackendInternalModelsResetPasswordRequest>,
  options?: SecondParameter<typeof createAuthInstance>
) => {
  return createAuthInstance<void>(
    {
      url: `/reset-password`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: goChatBackendInternalModelsResetPasswordRequest,
    },
    options
  );
};

/**
 * Authenticate a user using email and password. Sets auth_token and refresh_token cookies.
 * @summary Sign in a user
 */
export const postSignIn = (
  goChatBackendInternalModelsSignInRequest: BodyType<GoChatBackendInternalModelsSignInRequest>,
  options?: SecondParameter<typeof createAuthInstance>
) => {
  return createAuthInstance<GoChatBackendInternalModelsAuthResponse>(
    {
      url: `/sign-in`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: goChatBackendInternalModelsSignInRequest,
    },
    options
  );
};

/**
 * Verify the authentication code sent to user's email. Sets auth_token and refresh_token cookies.
 * @summary Verify authentication code
 */
export const postVerifyCode = (
  goChatBackendInternalModelsVerifyAuthIntentRequest: BodyType<GoChatBackendInternalModelsVerifyAuthIntentRequest>,
  options?: SecondParameter<typeof createAuthInstance>
) => {
  return createAuthInstance<GoChatBackendInternalModelsUser>(
    {
      url: `/verify-code`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: goChatBackendInternalModelsVerifyAuthIntentRequest,
    },
    options
  );
};

export type GetAuthSettingsResult = NonNullable<
  Awaited<ReturnType<typeof getAuthSettings>>
>;
export type PatchAuthSettingsResult = NonNullable<
  Awaited<ReturnType<typeof patchAuthSettings>>
>;
export type PatchChangeEmailResult = NonNullable<
  Awaited<ReturnType<typeof patchChangeEmail>>
>;
export type PatchChangePasswordResult = NonNullable<
  Awaited<ReturnType<typeof patchChangePassword>>
>;
export type PostForgotPasswordResult = NonNullable<
  Awaited<ReturnType<typeof postForgotPassword>>
>;
export type PostLogoutResult = NonNullable<
  Awaited<ReturnType<typeof postLogout>>
>;
export type PostRefreshResult = NonNullable<
  Awaited<ReturnType<typeof postRefresh>>
>;
export type PostRegisterResult = NonNullable<
  Awaited<ReturnType<typeof postRegister>>
>;
export type PostResendCodeResult = NonNullable<
  Awaited<ReturnType<typeof postResendCode>>
>;
export type PostResetPasswordResult = NonNullable<
  Awaited<ReturnType<typeof postResetPassword>>
>;
export type PostSignInResult = NonNullable<
  Awaited<ReturnType<typeof postSignIn>>
>;
export type PostVerifyCodeResult = NonNullable<
  Awaited<ReturnType<typeof postVerifyCode>>
>;

export const getGetAuthSettingsResponseMock = (
  overrideResponse: Partial<GoChatBackendInternalModelsSettings> = {}
): GoChatBackendInternalModelsSettings => ({
  id: faker.helpers.arrayElement([faker.string.alpha(20), undefined]),
  phone: faker.helpers.arrayElement([faker.string.alpha(20), undefined]),
  email: faker.helpers.arrayElement([faker.string.alpha(20), undefined]),
  locale: faker.helpers.arrayElement([faker.string.alpha(20), undefined]),
  timezone: faker.helpers.arrayElement([faker.string.alpha(20), undefined]),
  isActive: faker.helpers.arrayElement([faker.datatype.boolean(), undefined]),
  password_changed_at: faker.helpers.arrayElement([
    `${faker.date.past().toISOString().split('.')[0]}Z`,
    undefined,
  ]),
  need_to_change_password: faker.helpers.arrayElement([
    faker.datatype.boolean(),
    undefined,
  ]),
  twofa_is_active: faker.helpers.arrayElement([
    faker.datatype.boolean(),
    undefined,
  ]),
  ...overrideResponse,
});

export const getPatchChangeEmailResponseMock = (): string =>
  faker.word.sample();

export const getPatchChangePasswordResponseMock = (): string =>
  faker.word.sample();

export const getPostForgotPasswordResponseMock = (
  overrideResponse: Partial<ForgotPasswordResponse> = {}
): ForgotPasswordResponse => ({
  token: faker.string.alpha(20),
  ...overrideResponse,
});

export const getPostRegisterResponseMock = (
  overrideResponse: Partial<GoChatBackendInternalModelsAuthResponse> = {}
): GoChatBackendInternalModelsAuthResponse => ({
  auth_token: faker.helpers.arrayElement([faker.string.alpha(20), undefined]),
  auth_intent: faker.helpers.arrayElement([
    {
      user: faker.helpers.arrayElement([
        {
          account_id: faker.helpers.arrayElement([
            faker.string.alpha(20),
            undefined,
          ]),
          balance: faker.helpers.arrayElement([
            faker.number.int({ min: undefined, max: undefined }),
            undefined,
          ]),
          created_at: faker.helpers.arrayElement([
            faker.string.alpha(20),
            undefined,
          ]),
          email: faker.helpers.arrayElement([
            faker.string.alpha(20),
            undefined,
          ]),
          first_name: faker.helpers.arrayElement([
            faker.string.alpha(20),
            undefined,
          ]),
          id: faker.helpers.arrayElement([faker.string.alpha(20), undefined]),
          is_active: faker.helpers.arrayElement([
            faker.datatype.boolean(),
            undefined,
          ]),
          last_activity_at: faker.helpers.arrayElement([
            faker.string.alpha(20),
            undefined,
          ]),
          last_name: faker.helpers.arrayElement([
            faker.string.alpha(20),
            undefined,
          ]),
          locale: faker.helpers.arrayElement([
            faker.string.alpha(20),
            undefined,
          ]),
          need_to_change_password: faker.helpers.arrayElement([
            faker.datatype.boolean(),
            undefined,
          ]),
          password_changed_at: faker.helpers.arrayElement([
            faker.string.alpha(20),
            undefined,
          ]),
          phone: faker.helpers.arrayElement([
            faker.string.alpha(20),
            undefined,
          ]),
          role: faker.helpers.arrayElement([
            faker.helpers.arrayElement(Object.values(GoChatBackendPkgRbacRole)),
            undefined,
          ]),
          timezone: faker.helpers.arrayElement([
            faker.string.alpha(20),
            undefined,
          ]),
          f2a_is_active: faker.helpers.arrayElement([
            faker.datatype.boolean(),
            undefined,
          ]),
        },
        undefined,
      ]),
    },
    undefined,
  ]),
  ...overrideResponse,
});

export const getPostSignInResponseMock = (
  overrideResponse: Partial<GoChatBackendInternalModelsAuthResponse> = {}
): GoChatBackendInternalModelsAuthResponse => ({
  auth_token: faker.helpers.arrayElement([faker.string.alpha(20), undefined]),
  auth_intent: faker.helpers.arrayElement([
    {
      user: faker.helpers.arrayElement([
        {
          account_id: faker.helpers.arrayElement([
            faker.string.alpha(20),
            undefined,
          ]),
          balance: faker.helpers.arrayElement([
            faker.number.int({ min: undefined, max: undefined }),
            undefined,
          ]),
          created_at: faker.helpers.arrayElement([
            faker.string.alpha(20),
            undefined,
          ]),
          email: faker.helpers.arrayElement([
            faker.string.alpha(20),
            undefined,
          ]),
          first_name: faker.helpers.arrayElement([
            faker.string.alpha(20),
            undefined,
          ]),
          id: faker.helpers.arrayElement([faker.string.alpha(20), undefined]),
          is_active: faker.helpers.arrayElement([
            faker.datatype.boolean(),
            undefined,
          ]),
          last_activity_at: faker.helpers.arrayElement([
            faker.string.alpha(20),
            undefined,
          ]),
          last_name: faker.helpers.arrayElement([
            faker.string.alpha(20),
            undefined,
          ]),
          locale: faker.helpers.arrayElement([
            faker.string.alpha(20),
            undefined,
          ]),
          need_to_change_password: faker.helpers.arrayElement([
            faker.datatype.boolean(),
            undefined,
          ]),
          password_changed_at: faker.helpers.arrayElement([
            faker.string.alpha(20),
            undefined,
          ]),
          phone: faker.helpers.arrayElement([
            faker.string.alpha(20),
            undefined,
          ]),
          role: faker.helpers.arrayElement([
            faker.helpers.arrayElement(Object.values(GoChatBackendPkgRbacRole)),
            undefined,
          ]),
          timezone: faker.helpers.arrayElement([
            faker.string.alpha(20),
            undefined,
          ]),
          f2a_is_active: faker.helpers.arrayElement([
            faker.datatype.boolean(),
            undefined,
          ]),
        },
        undefined,
      ]),
    },
    undefined,
  ]),
  ...overrideResponse,
});

export const getPostVerifyCodeResponseMock = (
  overrideResponse: Partial<GoChatBackendInternalModelsUser> = {}
): GoChatBackendInternalModelsUser => ({
  account_id: faker.helpers.arrayElement([faker.string.alpha(20), undefined]),
  balance: faker.helpers.arrayElement([
    faker.number.int({ min: undefined, max: undefined }),
    undefined,
  ]),
  created_at: faker.helpers.arrayElement([faker.string.alpha(20), undefined]),
  email: faker.helpers.arrayElement([faker.string.alpha(20), undefined]),
  first_name: faker.helpers.arrayElement([faker.string.alpha(20), undefined]),
  id: faker.helpers.arrayElement([faker.string.alpha(20), undefined]),
  is_active: faker.helpers.arrayElement([faker.datatype.boolean(), undefined]),
  last_activity_at: faker.helpers.arrayElement([
    faker.string.alpha(20),
    undefined,
  ]),
  last_name: faker.helpers.arrayElement([faker.string.alpha(20), undefined]),
  locale: faker.helpers.arrayElement([faker.string.alpha(20), undefined]),
  need_to_change_password: faker.helpers.arrayElement([
    faker.datatype.boolean(),
    undefined,
  ]),
  password_changed_at: faker.helpers.arrayElement([
    faker.string.alpha(20),
    undefined,
  ]),
  phone: faker.helpers.arrayElement([faker.string.alpha(20), undefined]),
  role: faker.helpers.arrayElement([
    faker.helpers.arrayElement(Object.values(GoChatBackendPkgRbacRole)),
    undefined,
  ]),
  timezone: faker.helpers.arrayElement([faker.string.alpha(20), undefined]),
  f2a_is_active: faker.helpers.arrayElement([
    faker.datatype.boolean(),
    undefined,
  ]),
  ...overrideResponse,
});

export const getGetAuthSettingsMockHandler = (
  overrideResponse?:
    | GoChatBackendInternalModelsSettings
    | ((
        info: Parameters<Parameters<typeof http.get>[1]>[0]
      ) =>
        | Promise<GoChatBackendInternalModelsSettings>
        | GoChatBackendInternalModelsSettings)
) => {
  return http.get('*/auth/settings', async (info) => {
    await delay(1000);

    return new HttpResponse(
      JSON.stringify(
        overrideResponse !== undefined
          ? typeof overrideResponse === 'function'
            ? await overrideResponse(info)
            : overrideResponse
          : getGetAuthSettingsResponseMock()
      ),
      { status: 200, headers: { 'Content-Type': 'application/json' } }
    );
  });
};

export const getPatchAuthSettingsMockHandler = (
  overrideResponse?:
    | void
    | ((
        info: Parameters<Parameters<typeof http.patch>[1]>[0]
      ) => Promise<void> | void)
) => {
  return http.patch('*/auth/settings', async (info) => {
    await delay(1000);
    if (typeof overrideResponse === 'function') {
      await overrideResponse(info);
    }
    return new HttpResponse(null, { status: 204 });
  });
};

export const getPatchChangeEmailMockHandler = (
  overrideResponse?:
    | string
    | ((
        info: Parameters<Parameters<typeof http.patch>[1]>[0]
      ) => Promise<string> | string)
) => {
  return http.patch('*/change-email', async (info) => {
    await delay(1000);

    return new HttpResponse(
      JSON.stringify(
        overrideResponse !== undefined
          ? typeof overrideResponse === 'function'
            ? await overrideResponse(info)
            : overrideResponse
          : getPatchChangeEmailResponseMock()
      ),
      { status: 204, headers: { 'Content-Type': 'application/json' } }
    );
  });
};

export const getPatchChangePasswordMockHandler = (
  overrideResponse?:
    | string
    | ((
        info: Parameters<Parameters<typeof http.patch>[1]>[0]
      ) => Promise<string> | string)
) => {
  return http.patch('*/change-password', async (info) => {
    await delay(1000);

    return new HttpResponse(
      JSON.stringify(
        overrideResponse !== undefined
          ? typeof overrideResponse === 'function'
            ? await overrideResponse(info)
            : overrideResponse
          : getPatchChangePasswordResponseMock()
      ),
      { status: 204, headers: { 'Content-Type': 'application/json' } }
    );
  });
};

export const getPostForgotPasswordMockHandler = (
  overrideResponse?:
    | ForgotPasswordResponse
    | ((
        info: Parameters<Parameters<typeof http.post>[1]>[0]
      ) => Promise<ForgotPasswordResponse> | ForgotPasswordResponse)
) => {
  return http.post('*/forgot-password', async (info) => {
    await delay(1000);

    return new HttpResponse(
      JSON.stringify(
        overrideResponse !== undefined
          ? typeof overrideResponse === 'function'
            ? await overrideResponse(info)
            : overrideResponse
          : getPostForgotPasswordResponseMock()
      ),
      { status: 200, headers: { 'Content-Type': 'application/json' } }
    );
  });
};

export const getPostLogoutMockHandler = (
  overrideResponse?:
    | void
    | ((
        info: Parameters<Parameters<typeof http.post>[1]>[0]
      ) => Promise<void> | void)
) => {
  return http.post('*/logout', async (info) => {
    await delay(1000);
    if (typeof overrideResponse === 'function') {
      await overrideResponse(info);
    }
    return new HttpResponse(null, { status: 204 });
  });
};

export const getPostRefreshMockHandler = (
  overrideResponse?:
    | void
    | ((
        info: Parameters<Parameters<typeof http.post>[1]>[0]
      ) => Promise<void> | void)
) => {
  return http.post('*/refresh', async (info) => {
    await delay(1000);
    if (typeof overrideResponse === 'function') {
      await overrideResponse(info);
    }
    return new HttpResponse(null, { status: 204 });
  });
};

export const getPostRegisterMockHandler = (
  overrideResponse?:
    | GoChatBackendInternalModelsAuthResponse
    | ((
        info: Parameters<Parameters<typeof http.post>[1]>[0]
      ) =>
        | Promise<GoChatBackendInternalModelsAuthResponse>
        | GoChatBackendInternalModelsAuthResponse)
) => {
  return http.post('*/register', async (info) => {
    await delay(1000);

    return new HttpResponse(
      JSON.stringify(
        overrideResponse !== undefined
          ? typeof overrideResponse === 'function'
            ? await overrideResponse(info)
            : overrideResponse
          : getPostRegisterResponseMock()
      ),
      { status: 200, headers: { 'Content-Type': 'application/json' } }
    );
  });
};

export const getPostResendCodeMockHandler = (
  overrideResponse?:
    | void
    | ((
        info: Parameters<Parameters<typeof http.post>[1]>[0]
      ) => Promise<void> | void)
) => {
  return http.post('*/resend-code', async (info) => {
    await delay(1000);
    if (typeof overrideResponse === 'function') {
      await overrideResponse(info);
    }
    return new HttpResponse(null, { status: 204 });
  });
};

export const getPostResetPasswordMockHandler = (
  overrideResponse?:
    | void
    | ((
        info: Parameters<Parameters<typeof http.post>[1]>[0]
      ) => Promise<void> | void)
) => {
  return http.post('*/reset-password', async (info) => {
    await delay(1000);
    if (typeof overrideResponse === 'function') {
      await overrideResponse(info);
    }
    return new HttpResponse(null, { status: 204 });
  });
};

export const getPostSignInMockHandler = (
  overrideResponse?:
    | GoChatBackendInternalModelsAuthResponse
    | ((
        info: Parameters<Parameters<typeof http.post>[1]>[0]
      ) =>
        | Promise<GoChatBackendInternalModelsAuthResponse>
        | GoChatBackendInternalModelsAuthResponse)
) => {
  return http.post('*/sign-in', async (info) => {
    await delay(1000);

    return new HttpResponse(
      JSON.stringify(
        overrideResponse !== undefined
          ? typeof overrideResponse === 'function'
            ? await overrideResponse(info)
            : overrideResponse
          : getPostSignInResponseMock()
      ),
      { status: 200, headers: { 'Content-Type': 'application/json' } }
    );
  });
};

export const getPostVerifyCodeMockHandler = (
  overrideResponse?:
    | GoChatBackendInternalModelsUser
    | ((
        info: Parameters<Parameters<typeof http.post>[1]>[0]
      ) =>
        | Promise<GoChatBackendInternalModelsUser>
        | GoChatBackendInternalModelsUser)
) => {
  return http.post('*/verify-code', async (info) => {
    await delay(1000);

    return new HttpResponse(
      JSON.stringify(
        overrideResponse !== undefined
          ? typeof overrideResponse === 'function'
            ? await overrideResponse(info)
            : overrideResponse
          : getPostVerifyCodeResponseMock()
      ),
      { status: 200, headers: { 'Content-Type': 'application/json' } }
    );
  });
};
export const getGoChatAuthAPIMock = () => [
  getGetAuthSettingsMockHandler(),
  getPatchAuthSettingsMockHandler(),
  getPatchChangeEmailMockHandler(),
  getPatchChangePasswordMockHandler(),
  getPostForgotPasswordMockHandler(),
  getPostLogoutMockHandler(),
  getPostRefreshMockHandler(),
  getPostRegisterMockHandler(),
  getPostResendCodeMockHandler(),
  getPostResetPasswordMockHandler(),
  getPostSignInMockHandler(),
  getPostVerifyCodeMockHandler(),
];
